import {Box, Button, Flex, HStack, Icon, Input, Text, VStack} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash, HiOutlineChevronDown, HiOutlineChevronUp } from "react-icons/hi2";
import { useState } from "react";
import MessageEditor from "./MessageEditor";

const createEmptyMessage = () => ({
    input: null,
    output: { exactResponse: null, possibleResponses: [], isPositive: true, expectedEntityId: "" }
});

const IncidentEditor = ({ incident, onChange, onRemove, index, collapsed, onToggleCollapsed }) => {
    const [collapsedMessages, setCollapsedMessages] = useState(new Set());
    const update = (patch) => onChange({ ...incident, ...patch });

    const addMessage = () => {
        update({ messages: [...incident.messages, createEmptyMessage()] });
    };

    const updateMessage = (i, msg) => {
        const updated = [...incident.messages];
        updated[i] = msg;
        update({ messages: updated });
    };

    const removeMessage = (i) => {
        update({ messages: incident.messages.filter((_, idx) => idx !== i) });
        setCollapsedMessages((prev) => {
            const next = new Set();
            prev.forEach((idx) => { if (idx < i) next.add(idx); else if (idx > i) next.add(idx - 1); });
            return next;
        });
    };

    const toggleMessageCollapsed = (i, altKey) => {
        if (altKey) {
            const allCollapsed = incident.messages.every((_, idx) => collapsedMessages.has(idx));
            setCollapsedMessages(allCollapsed ? new Set() : new Set(incident.messages.map((_, idx) => idx)));
        } else {
            setCollapsedMessages((prev) => {
                const next = new Set(prev);
                next.has(i) ? next.delete(i) : next.add(i);
                return next;
            });
        }
    };

    const collapseAllMessages = () => setCollapsedMessages(new Set(incident.messages.map((_, i) => i)));

    return (
        <Box w="full" bg="bg.secondary" borderRadius={12} p={5} borderWidth="1px" borderColor="border.primary">
            <Flex justifyContent="space-between" alignItems="center" mb={collapsed ? 0 : 4}>
                <Flex alignItems="center" gap={2} cursor="pointer" onClick={onToggleCollapsed} flex={1} userSelect="none">
                    <Icon fontSize={16} color="text.grey" style={{ transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform 0.15s" }}>
                        <HiOutlineChevronDown />
                    </Icon>
                    <Text color="white" fontSize={16} fontWeight="semibold">
                        Incident #{index + 1}{incident.name ? ` — ${incident.name}` : ""}
                    </Text>
                </Flex>
                <Button variant="outlined" size="sm" onClick={onRemove} colorScheme="red" borderColor="red.400" color="red.400" _hover={{ bg: "red.900" }}>
                    <Icon fontSize={16}><HiOutlineTrash /></Icon>
                    Remove
                </Button>
            </Flex>

            {!collapsed && <VStack gap={4} alignItems="flex-start" w="full">
                <HStack w="full" gap={4}>
                    <Box w="full">
                        <Text color="white" fontSize={13} mb={1}>Scenario ID</Text>
                        <Input
                            variant="primary"
                            placeholder="UUID scenariusza"
                            value={incident.scenarioId}
                            onChange={(e) => update({ scenarioId: e.target.value })}
                        />
                    </Box>
                    <Box w="full">
                        <Text color="white" fontSize={13} mb={1}>Recruitment ID</Text>
                        <Input
                            variant="primary"
                            placeholder="UUID rekrutacji"
                            value={incident.recruitmentId}
                            onChange={(e) => update({ recruitmentId: e.target.value })}
                        />
                    </Box>
                    <Box w="full">
                        <Text color="white" fontSize={13} mb={1}>Name</Text>
                        <Input
                            variant="primary"
                            placeholder="Nazwa incydentu"
                            value={incident.name}
                            onChange={(e) => update({ name: e.target.value })}
                        />
                    </Box>
                </HStack>

                {/* Messages */}
                <Box w="full">
                    <Flex justifyContent="space-between" alignItems="center" mb={3}>
                        <Text color="white" fontSize={14} fontWeight="semibold">Messages</Text>
                        <HStack gap={2}>
                            {incident.messages.length > 1 && (
                                <Button size="sm" variant="outlined" onClick={collapseAllMessages}>
                                    <Icon fontSize={14}><HiOutlineChevronUp /></Icon>
                                    Zwiń wszystkie
                                </Button>
                            )}
                            <Button size="sm" variant="outlined" onClick={addMessage}>
                                <Icon fontSize={16}><HiOutlinePlus /></Icon>
                                Add Message
                            </Button>
                        </HStack>
                    </Flex>
                    <VStack gap={3} alignItems="flex-start" w="full">
                        {incident.messages.map((msg, i) => (
                            <MessageEditor
                                key={i}
                                index={i}
                                message={msg}
                                onChange={(updated) => updateMessage(i, updated)}
                                onRemove={() => removeMessage(i)}
                                collapsed={collapsedMessages.has(i)}
                                onToggleCollapsed={(e) => toggleMessageCollapsed(i, e.altKey)}
                            />
                        ))}
                        {incident.messages.length === 0 && (
                            <Text color="text.grey" fontSize={13} fontStyle="italic">Brak wiadomości — dodaj przynajmniej jedną</Text>
                        )}
                    </VStack>
                </Box>
            </VStack>}
        </Box>
    );
};

export default IncidentEditor;
